// SaveSystem — manual save slots, a rolling auto-save, and JSON export/import
// on top of the automatic Zustand persistence. Pure TS, browser localStorage.

const GAME_KEY = 'padel-manager-save'
const ACHIEVEMENTS_KEY = 'padel-achievements'
const CAREER_KEY = 'padel-manager-career'
const SLOT_PREFIX = 'padel-manager-slot-'
const AUTOSAVE_KEY = `${SLOT_PREFIX}auto`
const SAVE_VERSION = 2

export const SAVE_SLOTS = [1, 2, 3] as const
export type SaveSlot = (typeof SAVE_SLOTS)[number]

/** The display fields shared by manual slots and the auto-save */
export interface SaveMeta {
  savedAt: number // epoch ms
  day: number
  money: number
  clubLevel: number
}

export interface SlotMetadata extends SaveMeta {
  slot: SaveSlot
}

interface SaveBundle {
  version: number
  savedAt: number
  game: string // raw persisted gameStore JSON
  achievements: string | null // raw persisted achievements JSON
  career?: string | null // raw persisted careerStore JSON (v2+)
}

function slotKey(slot: SaveSlot): string {
  return `${SLOT_PREFIX}${slot}`
}

/** Snapshot the live persisted stores into a bundle (null if no game exists) */
function buildLiveBundle(): SaveBundle | null {
  const game = localStorage.getItem(GAME_KEY)
  if (!game) return null
  return {
    version: SAVE_VERSION,
    savedAt: Date.now(),
    game,
    achievements: localStorage.getItem(ACHIEVEMENTS_KEY),
    career: localStorage.getItem(CAREER_KEY),
  }
}

function parseBundle(raw: string | null): SaveBundle | null {
  if (!raw) return null
  try {
    return JSON.parse(raw) as SaveBundle
  } catch {
    return null
  }
}

function bundleMeta(bundle: SaveBundle | null): SaveMeta | null {
  if (!bundle) return null
  try {
    const state = JSON.parse(bundle.game)?.state
    return {
      savedAt: bundle.savedAt,
      day: state?.day ?? 0,
      money: state?.money ?? 0,
      clubLevel: state?.clubLevel ?? 0,
    }
  } catch {
    return null
  }
}

/** Restore a bundle into the live keys. Caller must reload so stores rehydrate. */
function restoreBundle(bundle: SaveBundle | null): boolean {
  if (!bundle?.game) return false
  localStorage.setItem(GAME_KEY, bundle.game)
  if (bundle.achievements) localStorage.setItem(ACHIEVEMENTS_KEY, bundle.achievements)
  if (bundle.career) localStorage.setItem(CAREER_KEY, bundle.career)
  return true
}

/** Copy the live save into a manual slot */
export function saveToSlot(slot: SaveSlot): SlotMetadata | null {
  const bundle = buildLiveBundle()
  if (!bundle) return null
  localStorage.setItem(slotKey(slot), JSON.stringify(bundle))
  const meta = bundleMeta(bundle)
  return meta ? { slot, ...meta } : null
}

export function getSlotMetadata(slot: SaveSlot): SlotMetadata | null {
  const meta = bundleMeta(parseBundle(localStorage.getItem(slotKey(slot))))
  return meta ? { slot, ...meta } : null
}

export function listSlots(): Array<SlotMetadata | null> {
  return SAVE_SLOTS.map(getSlotMetadata)
}

export function deleteSlot(slot: SaveSlot): void {
  localStorage.removeItem(slotKey(slot))
}

/**
 * Restore a slot into the live save. The caller must reload the page
 * afterwards so the Zustand stores rehydrate from storage.
 */
export function loadSlot(slot: SaveSlot): boolean {
  return restoreBundle(parseBundle(localStorage.getItem(slotKey(slot))))
}

// --- Auto-save: a single rolling snapshot written by AutoSaveSystem ----------

/** Write the live game into the rolling auto-save (overwrites the previous one) */
export function writeAutoSave(): SaveMeta | null {
  const bundle = buildLiveBundle()
  if (!bundle) return null
  localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(bundle))
  return bundleMeta(bundle)
}

export function getAutoSaveMetadata(): SaveMeta | null {
  return bundleMeta(parseBundle(localStorage.getItem(AUTOSAVE_KEY)))
}

/** Restore the auto-save into the live save (caller must reload afterwards) */
export function loadAutoSave(): boolean {
  return restoreBundle(parseBundle(localStorage.getItem(AUTOSAVE_KEY)))
}

export function deleteAutoSave(): void {
  localStorage.removeItem(AUTOSAVE_KEY)
}

/** Serialize the live save for download */
export function exportSave(): string | null {
  const bundle = buildLiveBundle()
  return bundle ? JSON.stringify(bundle, null, 2) : null
}

/**
 * Validate and install an exported save. The caller must reload the
 * page afterwards. Returns an error message in Spanish, or null on success.
 */
export function importSave(json: string): string | null {
  let bundle: SaveBundle
  try {
    bundle = JSON.parse(json)
  } catch {
    return 'El archivo no es un JSON valido'
  }

  if (typeof bundle?.version !== 'number' || bundle.version > SAVE_VERSION) {
    return 'Version de guardado no compatible'
  }
  if (typeof bundle.game !== 'string') {
    return 'El archivo no contiene una partida'
  }

  try {
    const state = JSON.parse(bundle.game)?.state
    if (typeof state?.day !== 'number' || typeof state?.money !== 'number') {
      return 'La partida esta corrupta o incompleta'
    }
  } catch {
    return 'La partida esta corrupta o incompleta'
  }

  restoreBundle(bundle)
  return null
}
